/**
 * The app's own landing page counts visits the same way popin.buzz does
 * (landing/t.js, docs/SPEC_TRAFFIC.md): one anonymous beacon to /v1/t, a
 * random visitor id in localStorage, nothing when Do Not Track or Global
 * Privacy Control is on.
 */
const ID_RE = /^[A-Za-z0-9_-]{8,64}$/;

function keep(store: Storage, key: string, prefix: string): string {
  const make = () => prefix + Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, '0')).join('');
  try {
    let v = store.getItem(key);
    if (!v) {
      v = make();
      store.setItem(key, v);
    }
    return v;
  } catch {
    return make();
  }
}

/**
 * `path` overrides the page's own path, so steps without a URL of their own
 * (each onboarding step, "signed up") show as pages in Admin → Traffic (site
 * app) and read as a funnel: / → /login → signup → /onboarding/* → done.
 */
export function landingBeacon(kind: 'view' | 'click' | 'signup' | 'engage' | 'step' = 'view', seconds = 0, path?: string, step?: { step: string; detail?: string }): void {
  try {
    const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
    if (nav.globalPrivacyControl || nav.doNotTrack === '1' || typeof nav.sendBeacon !== 'function') return;
    adoptVisitor();
    const q = new URLSearchParams(location.search);
    const utm = Object.fromEntries(['source', 'medium', 'campaign', 'term', 'content'].map((k) => [k, q.get(`utm_${k}`) ?? '']));
    const body = JSON.stringify({
      kind,
      seconds,
      site: 'app',
      path: path ?? location.pathname,
      visitor: keep(localStorage, 'popin-v', 'v_'),
      session: keep(sessionStorage, 'popin-s', 's_'),
      referrer: document.referrer,
      utm,
      ref: q.get('ref') ?? q.get('tag') ?? q.get('via') ?? '',
      lang: navigator.language,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
      ...(step ? { step: step.step, detail: step.detail ?? '' } : {}),
    });
    nav.sendBeacon('/v1/t', new Blob([body], { type: 'text/plain' }));
  } catch {
    /* analytics never breaks the page */
  }
}

/** Time on page: visible seconds, reported each time the page is hidden. Returns a cleanup. */
export function watchEngagement(): () => void {
  let since = document.visibilityState === 'visible' ? Date.now() : 0;
  const flush = () => {
    if (!since) return;
    const s = Math.round((Date.now() - since) / 1000);
    since = 0;
    if (s > 0) landingBeacon('engage', Math.min(s, 1800));
  };
  const onVis = () => {
    if (document.visibilityState === 'hidden') flush();
    else if (!since) since = Date.now();
  };
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('pagehide', flush);
  return () => {
    flush(); // leaving the landing for sign-in counts too
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('pagehide', flush);
  };
}

/**
 * popin.buzz keeps its own visitor id (another origin's localStorage). Its
 * "plan a date" buttons pass it along as `#pv=` (a hash never reaches a
 * server), and the app adopts it once, so Admin → Traffic → Try follows one
 * visitor from the landing page to their date. Never overwrites an id.
 */
function adoptVisitor(): void {
  try {
    const pv = new URLSearchParams(location.hash.slice(1)).get('pv');
    if (pv && ID_RE.test(pv) && !localStorage.getItem('popin-v')) localStorage.setItem('popin-v', pv);
  } catch {
    /* storage blocked */
  }
}

/** This browser's anonymous visitor id (the one the beacons send). */
export function visitorId(): string {
  adoptVisitor();
  try {
    return keep(localStorage, 'popin-v', 'v_');
  } catch {
    return '';
  }
}

/**
 * One step of the /try guest funnel (docs/SPEC_TRY.md): an allow-listed
 * step name, an optional short code (`hinge`, `timer`, `whatsapp`) and the
 * visible seconds spent on /try so far. Never free text.
 */
export function tryStep(step: string, detail = '', seconds = 0): void {
  landingBeacon('step', Math.max(0, Math.round(seconds)), '/try', { step, detail });
}
